import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/features/shell/AppShell";
import { MailPage } from "@/features/mail/MailPage";
import { MessageFullView } from "@/features/mail/MessageFullView";
import { PeoplePage } from "@/features/people/PeoplePage";
import { TasksPage } from "@/features/tasks/TasksPage";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { CallbackPage } from "@/auth/CallbackPage";
import { SilentRenewPage } from "@/auth/SilentRenewPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { GeneralPage } from "@/features/settings/GeneralPage";
import { NotificationsPage } from "@/features/settings/NotificationsPage";
import { MailClientPage } from "@/features/settings/MailClientPage";
import { IdentitiesPage } from "@/features/settings/IdentitiesPage";
import { LabelsPage } from "@/features/settings/LabelsPage";
import { FiltersPage } from "@/features/settings/FiltersPage";
import { AutoRespondersPage } from "@/features/settings/AutoRespondersPage";
import { VacationPage } from "@/features/settings/VacationPage";
import { TemplatesPage } from "@/features/settings/TemplatesPage";
import { ShortcutsPage } from "@/features/settings/ShortcutsPage";

// Calendar pulls in @syncfusion/ej2-react-schedule, a sizeable chunk.
// Route-level code-split keeps it out of the initial Mail bundle.
const CalendarPage = lazy(() =>
    import("@/features/calendar/CalendarPage").then((m) => ({ default: m.CalendarPage }))
);

function CalendarRoute() {
    return (
        <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading calendar…</div>}>
            <CalendarPage />
        </Suspense>
    );
}

// Wraps every signed-in route so an anonymous user is bounced to the
// Identity Service before the shell even mounts. The callback and silent
// renew routes stay outside the guard since they complete the sign-in.
function ProtectedShell() {
    return (
        <ProtectedRoute>
            <AppShell />
        </ProtectedRoute>
    );
}

export function App() {
    return (
        <>
            <Routes>
                <Route path="/auth/callback" element={<CallbackPage />} />
                <Route path="/auth/silent-renew" element={<SilentRenewPage />} />
                <Route element={<ProtectedShell />}>
                    <Route path="/" element={<Navigate to="/mail" replace />} />
                    <Route path="/mail" element={<MailPage />} />
                    {/* Full-window single message. Double-clicking a row opens
                        this in a new tab, which is the only way to read a long
                        message comfortably when the reading pane is stacked. */}
                    <Route path="/mail/message/:messageId" element={<MessageFullView />} />
                    <Route path="/calendar" element={<CalendarRoute />} />
                    <Route path="/people" element={<PeoplePage />} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/settings" element={<SettingsPage />}>
                        <Route index element={<Navigate to="/settings/general" replace />} />
                        <Route path="general" element={<GeneralPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="mail-client" element={<MailClientPage />} />
                        <Route path="identities" element={<IdentitiesPage />} />
                        <Route path="labels" element={<LabelsPage />} />
                        <Route path="filters" element={<FiltersPage />} />
                        <Route path="auto-responders" element={<AutoRespondersPage />} />
                        <Route path="vacation" element={<VacationPage />} />
                        <Route path="templates" element={<TemplatesPage />} />
                        <Route path="shortcuts" element={<ShortcutsPage />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/mail" replace />} />
                </Route>
            </Routes>
        </>
    );
}
